import React, { useState } from "react";
import axios from "axios";
import { PhoneCall } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", phone: "", slot: "", topic: "" };

export default function CallbackButton() {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(empty);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || form.phone.replace(/\D/g, "").length < 6) {
      toast({ title: "Champs manquants", description: "Merci d'indiquer votre nom et un numéro de téléphone valide." });
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/callbacks`, form);
      toast({ title: "Demande enregistrée — merci !", description: "Un conseiller Solès Immo vous rappelle sous 24h." });
      setForm(empty);
      setOpen(false);
    } catch (err) {
      toast({ title: "Erreur", description: "Une erreur est survenue. Merci de réessayer ou de nous appeler." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="callback-floating-btn"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-accent text-accent-foreground pl-4 pr-5 h-14 shadow-2xl hover:-translate-y-0.5 transition-transform duration-300"
      >
        <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
          <span className="absolute inset-0 rounded-full bg-white/30 animate-ping" />
          <PhoneCall className="h-4 w-4 relative" />
        </span>
        <span className="font-medium text-sm hidden sm:inline">Être rappelé sous 24h</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="callback-dialog" className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Être rappelé sous 24h</DialogTitle>
            <DialogDescription>Laissez-nous vos coordonnées, un conseiller vous rappelle rapidement.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label>Nom complet *</Label>
              <Input data-testid="callback-name-input" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Votre nom" />
            </div>
            <div className="space-y-1.5">
              <Label>Téléphone *</Label>
              <Input data-testid="callback-phone-input" type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="06 .." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Sujet</Label>
                <Select value={form.topic} onValueChange={(v) => update("topic", v)}>
                  <SelectTrigger data-testid="callback-topic-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>
                    {["Achat", "Vente", "Location", "Gestion", "Estimation"].map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Créneau</Label>
                <Select value={form.slot} onValueChange={(v) => update("slot", v)}>
                  <SelectTrigger data-testid="callback-slot-select"><SelectValue placeholder="Indifférent" /></SelectTrigger>
                  <SelectContent>
                    {["Matin (9h30–12h)", "Après-midi (14h–17h)", "Fin de journée (17h–19h)"].map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button data-testid="callback-submit-btn" type="submit" disabled={loading} className="w-full h-12 bg-accent hover:bg-accent/90 text-accent-foreground rounded-full">
              {loading ? "Envoi..." : "Me faire rappeler"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
